import { LangChainTracer } from '@langchain/core/tracers/tracer_langchain';
import { Run, RunsInvokePayload } from '@langchain/langgraph-sdk';

import { useState } from 'react';

import useToast from '@/components/Toast/useToast';
import { ExampleSchema, SessionSchema } from '@/types/schema';

import { useLangGraphClient } from '../api';
import { useCreateStudioExperiment } from '../api/useGraphSwr';
import { STUDIO_METADATA } from '../constants';

export const useRunStudioExperiment = ({
  setExperimentId,
  setExperimentModalOpen,
}: {
  setExperimentId: (experimentId: string) => void;
  setExperimentModalOpen: (isOpen: boolean) => void;
}) => {
  const { createToast } = useToast();
  const [langgraph, apiUrl] = useLangGraphClient();
  const { trigger: createStudioExperiment, isMutating: creatingExperiment } =
    useCreateStudioExperiment();

  const [creatingExperimentOrRuns, setCreatingExperimentOrRuns] =
    useState(false);

  const [progress, setProgress] = useState({ success: 0, failed: 0, total: 0 });

  // Create thread and post run but don't wait for completion
  const postThreadAndRun = async ({
    example,
    experiment,
    assistantId,
  }: {
    example: ExampleSchema;
    experiment: SessionSchema;
    assistantId: string;
  }) => {
    try {
      const thread = await langgraph.threads.create({
        metadata: {
          ls_experiment_id: experiment.id,
        },
      });
      const payload: RunsInvokePayload = {
        input: example.inputs,
        metadata: {
          ...STUDIO_METADATA(apiUrl),
        },
        _langsmithTracer: {
          projectName: experiment.name,
          exampleId: example.id,
        } as LangChainTracer,
      };

      // Post the run in the background
      const run = await langgraph.runs.create(
        thread.thread_id,
        assistantId,
        payload
      );

      return {
        run,
        threadId: thread.thread_id,
      };
    } catch (error) {
      console.error(error);
      setProgress((prev) => ({ ...prev, failed: prev.failed + 1 }));
      createToast({
        title: 'Error',
        description: `Failed to create run for example ${example.id}`,
        type: 'error',
      });
    }
  };

  // Wait for run completion
  const waitForRun = async ({
    run,
    threadId,
  }: {
    run: Run;
    threadId: string;
  }) => {
    try {
      let runData = await langgraph.runs.get(threadId, run.run_id);

      // Poll until the run is no longer pending
      while (runData.status === 'running' || runData.status === 'pending') {
        // Wait a bit before polling again
        await new Promise((resolve) => setTimeout(resolve, 3000));
        runData = await langgraph.runs.get(threadId, run.run_id);
      }

      // question: how should we handle 'interrupted' here?
      if (runData.status === 'success') {
        setProgress((prev) => ({ ...prev, success: prev.success + 1 }));
      } else {
        setProgress((prev) => ({ ...prev, failed: prev.failed + 1 }));
      }
    } catch (error) {
      console.error(error);
      setProgress((prev) => ({ ...prev, failed: prev.failed + 1 }));
    }
  };

  const runInputs = async ({
    assistantId,
    examples,
    selectedDatasetId,
    experimentName,
  }: {
    assistantId: string;
    examples: ExampleSchema[];
    selectedDatasetId: string;
    experimentName: string;
  }) => {
    const experiment = await createStudioExperiment({
      json: {
        project_name: experimentName,
        dataset_id: selectedDatasetId,
      },
    });
    if (!experiment) {
      throw new Error('Failed to create experiment');
    }
    setExperimentId(experiment.id);
    setProgress({ success: 0, failed: 0, total: examples.length });

    setCreatingExperimentOrRuns(true);

    // First post all runs and collect their promises
    const postPromises = examples.map((example) =>
      postThreadAndRun({
        example,
        experiment,
        assistantId,
      })
    );

    // Wait for all posts to complete
    const runResults = await Promise.allSettled(postPromises);

    // Now we can close the modal since all requests have been posted
    setExperimentModalOpen(false);
    setCreatingExperimentOrRuns(false);

    // Then wait for all runs to complete
    const completionPromises = runResults.map((result) =>
      result.status === 'fulfilled' && result.value
        ? waitForRun(result.value)
        : Promise.resolve()
    );

    await Promise.allSettled(completionPromises);

    return experiment;
  };

  return {
    progress,
    creatingExperiment: creatingExperiment || creatingExperimentOrRuns,
    runInputs,
  };
};
